import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shouldRedirectOnLogout } from '@/components/auth';
import { useCurrentUserContext } from '@/contexts';
import { endSession } from '@/lib';
import { AccountDeletionBlockerKind } from '@/types/gql.generated';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  UnorderedList,
  useToast,
} from '@/ui';
import { getErrorMessage, isGQLErrorOfType } from '@/utils/errors';
import { useSettingsModal } from '../../../store';
import {
  createDeleteAccountSchema,
  type DeleteAccountFormValues,
} from './deleteAccount.schema';
import {
  type AccountDeletionPreview,
  useAccountDeletionPreview,
} from './hooks/useAccountDeletionPreview';
import { useDeleteAccount } from './hooks/useDeleteAccount';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const BLOCKER_HINT_KEYS = {
  [AccountDeletionBlockerKind.SharedOrganization]: 'hint_shared_organization',
  [AccountDeletionBlockerKind.PaidSharedOrganization]:
    'hint_paid_shared_organization',
} as const satisfies Record<AccountDeletionBlockerKind, string>;

export const DeleteAccountDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'help.delete_account',
  });
  const { t: tPlans } = useTranslation('settingsModal', {
    keyPrefix: 'subscription.plan_names',
  });
  const { currentUser } = useCurrentUserContext();
  const { close: closeSettings } = useSettingsModal();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    data: preview,
    isLoading,
    isError: isPreviewError,
    error: previewError,
    refetch,
  } = useAccountDeletionPreview(isOpen);
  const {
    mutate: deleteAccount,
    isPending,
    error: deleteError,
    reset: resetMutation,
  } = useDeleteAccount();

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<DeleteAccountFormValues>({
    defaultValues: { confirmation: '' },
    resolver: standardSchemaResolver(
      createDeleteAccountSchema(
        currentUser?.email ?? '',
        t('confirmation_mismatch')
      )
    ),
  });

  const handleClose = () => {
    resetForm();
    resetMutation();
    onClose();
  };

  const onSubmit = ({ confirmation }: DeleteAccountFormValues) => {
    deleteAccount(
      { confirmation },
      {
        onSuccess: () => {
          endSession();
          closeSettings();
          toast.notify(t('success_toast'));
          const { shouldRedirect, path } = shouldRedirectOnLogout();
          if (shouldRedirect) {
            navigate(path);
          }
        },
        onError: (error) => {
          // Something was shared with the user since the preview loaded; the
          // fresh preview lists it.
          if (isGQLErrorOfType(error, 'AccountDeletionBlocked')) {
            resetMutation();
            refetch();
          }
        },
      }
    );
  };

  const renderBlocked = (blockers: AccountDeletionPreview['blockers']) => (
    <Flex direction="column" gap="3">
      <Text>{t('blocked_intro')}</Text>
      <UnorderedList spacing="2">
        {blockers.map((blocker) => (
          <ListItem key={`${blocker.kind}-${blocker.id}`}>
            <Text fontWeight="bold">{blocker.title}</Text>
            <Text color="ink.soft" fontSize="sm">
              {t(BLOCKER_HINT_KEYS[blocker.kind])}
            </Text>
          </ListItem>
        ))}
      </UnorderedList>
    </Flex>
  );

  const renderConfirm = (preview: AccountDeletionPreview) => (
    <Flex
      as="form"
      direction="column"
      gap="4"
      id="delete-account-form"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Text>
        {t('warning', {
          schedules: t('schedule_count', { count: preview.scheduleCount }),
          planners: t('planner_count', { count: preview.experienceCount }),
        })}
      </Text>
      {preview.activeSubscriptions.length > 0 && (
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle fontWeight="normal">
            {t('subscription_warning', {
              plan: tPlans(preview.activeSubscriptions[0].plan),
            })}
          </AlertTitle>
        </Alert>
      )}
      {preview.sharedItems.length > 0 && (
        <Flex direction="column" gap="2">
          <Text>{t('shared_items_intro')}</Text>
          <UnorderedList spacing="1">
            {preview.sharedItems.map((item) => (
              <ListItem key={`${item.kind}-${item.id}`}>
                {item.title || t('untitled')}
              </ListItem>
            ))}
          </UnorderedList>
        </Flex>
      )}
      {preview.sharedSchedulesLosingFeeds > 0 && (
        <Text>
          {t('feeds_warning', { count: preview.sharedSchedulesLosingFeeds })}
        </Text>
      )}
      {deleteError && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle fontWeight="normal">
            {getErrorMessage(deleteError)}
          </AlertTitle>
        </Alert>
      )}
      <FormControl isInvalid={!!errors.confirmation}>
        <FormLabel htmlFor="delete-account-confirmation">
          {t('confirmation_label')}
        </FormLabel>
        <Input
          autoComplete="off"
          id="delete-account-confirmation"
          {...register('confirmation')}
        />
        <FormErrorMessage>{errors.confirmation?.message}</FormErrorMessage>
      </FormControl>
    </Flex>
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Center py="6">
          <Spinner />
        </Center>
      );
    }
    if (isPreviewError || !preview) {
      return (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle fontWeight="normal">
            {previewError ? getErrorMessage(previewError) : t('load_error')}
          </AlertTitle>
        </Alert>
      );
    }
    return preview.canDelete
      ? renderConfirm(preview)
      : renderBlocked(preview.blockers);
  };

  const canDelete = !!preview?.canDelete;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {preview && !canDelete ? t('blocked_title') : t('title')}
        </ModalHeader>
        <ModalBody>{renderBody()}</ModalBody>
        <ModalFooter gap="2">
          <Button
            size="ah"
            variant={canDelete ? 'ahGhost' : 'ahSecondary'}
            onClick={handleClose}
          >
            {canDelete ? t('cancel') : t('close')}
          </Button>
          {canDelete && (
            <Button
              colorScheme="destructive"
              form="delete-account-form"
              isLoading={isPending}
              size="ah"
              type="submit"
            >
              {t('submit')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
